import type { Exercise } from '@onflow/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Chip, ChipRow } from '../components/v2/Chip.js';
import { EmptyState } from '../components/v2/EmptyState.js';
import { FlowButton } from '../components/v2/FlowButton.js';
import { FlowCard } from '../components/v2/FlowCard.js';
import { FlowInput } from '../components/v2/FlowInput.js';
import { FlowStatus } from '../components/v2/FlowStatus.js';
import { PageHeader } from '../components/v2/PageHeader.js';
import { useMe } from '../hooks/useAuth.js';
import { useExercises } from '../hooks/useExercises.js';
import { useWorkoutHistory } from '../hooks/useStats.js';
import { formatDuration, formatWeight, muscleLabel, timeAgo } from '../lib/format.js';

export function History() {
  const me = useMe();
  const exercises = useExercises();
  const history = useWorkoutHistory(30);

  const workouts = useMemo(
    () => history.data?.pages.flatMap((page) => page.items) ?? [],
    [history.data?.pages],
  );
  const units = me.data?.profile.units ?? 'KG';

  const exerciseById = useMemo(() => {
    const map = new Map<string, Exercise>();
    for (const ex of exercises.data ?? []) map.set(ex.id, ex);
    return map;
  }, [exercises.data]);

  // Resolve a workout exercise's display name: catalog name when the id maps
  // to a catalog row, else the raw id (AI plan sessions can store a free-text
  // exercise_name as the id when no catalog match was found at start time).
  const exerciseName = (id: string): string => exerciseById.get(id)?.name ?? id;

  const [search, setSearch] = useState('');
  const [filterMuscle, setFilterMuscle] = useState<string>('');

  // Build muscle filter chips from whatever muscle groups appear in this
  // user's history, so we don't show empty chips for groups they've never
  // trained.
  const muscleGroups = useMemo(() => {
    const set = new Set<string>();
    for (const w of workouts) for (const m of w.muscleGroups ?? []) set.add(m);
    return Array.from(set).sort();
  }, [workouts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workouts.filter((w) => {
      if (q && !w.title.toLowerCase().includes(q)) return false;
      if (filterMuscle && !(w.muscleGroups ?? []).includes(filterMuscle)) return false;
      return true;
    });
  }, [workouts, search, filterMuscle]);

  const header = (
    <PageHeader
      eyebrow="Train"
      title="History"
      subtitle={`${filtered.length} workout${filtered.length === 1 ? '' : 's'}`}
    />
  );

  if (history.isLoading) {
    return (
      <section className="fade-in">
        {header}
        <EmptyState title="Loading history…" />
      </section>
    );
  }

  if (history.error) {
    return (
      <section className="fade-in">
        {header}
        <EmptyState
          tone="error"
          role="alert"
          title="Couldn't load history"
          body={history.error.message}
        />
      </section>
    );
  }

  return (
    <section className="fade-in">
      {header}

      {workouts.length > 0 && (
        <>
          <FlowInput
            type="search"
            placeholder="Search workouts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperStyle={{ marginBottom: 12 }}
          />

          {muscleGroups.length > 0 && (
            <ChipRow style={{ marginBottom: 16 }}>
              <Chip active={filterMuscle === ''} onClick={() => setFilterMuscle('')}>
                All
              </Chip>
              {muscleGroups.map((m) => (
                <Chip key={m} active={filterMuscle === m} onClick={() => setFilterMuscle(m)}>
                  {muscleLabel(m)}
                </Chip>
              ))}
            </ChipRow>
          )}
        </>
      )}

      {workouts.length === 0 ? (
        <EmptyState
          icon="dumbbell"
          title="No workouts yet"
          body="Finish a workout and it'll show up here with your sets, volume, and PRs."
          action={
            <Link to="/workout/new" style={{ textDecoration: 'none' }}>
              <FlowButton variant="primary">Start your first workout</FlowButton>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No workouts match"
          body="Try clearing the search or filter."
          action={
            <FlowButton
              variant="soft"
              onClick={() => {
                setSearch('');
                setFilterMuscle('');
              }}
            >
              Clear filters
            </FlowButton>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((w) => {
            const hasPRs = w.exercises.some((ex) => ex.sets.some((s) => s.isPersonalRecord));
            const preview = w.exercises.slice(0, 3);
            const moreCount = w.exercises.length - preview.length;
            return (
              <Link
                key={w.id}
                to={`/history/${w.id}`}
                aria-label={`View workout: ${w.title}`}
                style={{ textDecoration: 'none', display: 'block' }}
              >
                <FlowCard>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink-1)' }}>
                      {w.title}
                    </span>
                    {hasPRs && <FlowStatus state="on-track" label="PR" dot={false} />}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                    {timeAgo(w.date)}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                      marginTop: 10,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <span>{formatDuration(w.duration)}</span>
                    <span>{formatWeight(w.totalVolume, units)}</span>
                    <span>{w.totalSets} sets</span>
                  </div>

                  {preview.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {preview.map((ex, i) => (
                        <div key={i} style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                          {ex.sets.length} set{ex.sets.length === 1 ? '' : 's'}{' '}
                          {exerciseName(ex.exerciseId)}
                        </div>
                      ))}
                      {moreCount > 0 && (
                        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>
                          See {moreCount} more exercise{moreCount === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  )}

                  {(w.muscleGroups ?? []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      {w.muscleGroups.map((m) => (
                        <FlowStatus key={m} state="neutral" label={muscleLabel(m)} dot={false} />
                      ))}
                    </div>
                  )}
                </FlowCard>
              </Link>
            );
          })}
          {history.hasNextPage && (
            <FlowButton
              variant="soft"
              block
              disabled={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {history.isFetchingNextPage ? 'Loading…' : 'Load older workouts'}
            </FlowButton>
          )}
        </div>
      )}
    </section>
  );
}
